import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import {
  TPartyKey,
  TUserId,
  IJoinPartyError,
  TAreaKey,
  TParticipantInviteForUserMap,
} from '../../PartyClientResponse';
import { joinParty } from '../../controller/client/PartyClient';
import { getIdToken, onInvitesUpdate, onPartyUpdate } from '../../controller/PartyDB';
import { VideoControlProvider } from '../../controller/video/VideoControlContext';
import { useFunctionsEmulatorOnLocal } from '../../firebase/firebase';
import { AppMapProps, IAppState, setInvites } from '../../redux/reducer';
import { ActionType, useAppState } from '../../state/AppState';
import OnboardAnonymousUser from '../OnboardAnonymousUser';
import Notifications from './Notifications';
import PartyStage from './PartyStage';
import Loading from '../common/Loading';
import FourOhFour from '../FourOhFour';
import BannedFromParty from '../BannedFromParty';
import { APP_TITLE_BASE } from './AppConstants';
import HomeButton from '../common/HomeButton';
import { Input } from 'antd';
import PartyNotFound from '../PartyNotFound';

const PartyPage = ({
  defaultPartyKey,
  setInvitesAction,
}: IAppState & {
  defaultPartyKey?: string;
  setInvitesAction: (invites: TParticipantInviteForUserMap) => void;
}) => {
  useFunctionsEmulatorOnLocal();

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }

  const history = useHistory();
  const { partyKey: paramsPartyKey } = useParams<{ partyKey: TPartyKey }>();
  const query = useQuery();
  const vtp = query.get('vtp');
  const vfp = query.get('vfp');
  const round: boolean = query.get('round') === null || !!query.get('round');
  const joinAreaKey = query.get('area') as TAreaKey | null;

  const [joinStatus, setJoinStatus] = useState<'connecting' | 'connected' | 'waiting'>(
    'connecting'
  );
  const [joinError, setJoinError] = useState<IJoinPartyError | null>(null);
  const [password, setPassword] = useState<string>('');

  let partyKey: TPartyKey = defaultPartyKey as TPartyKey;
  if (!partyKey) {
    partyKey = paramsPartyKey;
  }
  if (!partyKey) {
    history.replace('/');
  }

  const { user, dispatch, party } = useAppState();

  useEffect(() => {
    if (party?.displayName) {
      document.title = `${APP_TITLE_BASE} | ${party.displayName}`;
    }
  }, [party?.displayName]);

  useEffect(() => {
    if (user && partyKey) {
      if (!party && joinStatus === 'connecting') {
        (async () => {
          const idToken = await getIdToken();
          const joinedParty = await joinParty({ idToken, key: partyKey as TPartyKey });
          if (
            joinedParty === 'no-authorization' ||
            joinedParty === 'not-found' ||
            joinedParty === 'banned' ||
            joinedParty === 'party-full'
          ) {
            setJoinStatus('connected');
            setJoinError(joinedParty);
          } else {
            setJoinStatus('connected');
            dispatch({ type: ActionType.SetParty, party: joinedParty });
          }
        })();
      }
    }
  }, [user, partyKey, party, history, joinStatus, dispatch]);

  useEffect(() => {
    if (!user) {
      return;
    }
    return onInvitesUpdate(user.uid as TUserId, (invites) => {
      setInvitesAction(invites);
    });
  }, [user, setInvitesAction]);

  useEffect(() => {
    if (party && partyKey) {
      const detachListener = onPartyUpdate(party.key, async (updatedParty) => {
        if (
          party &&
          updatedParty &&
          updatedParty.key &&
          JSON.stringify(updatedParty) !== JSON.stringify(party)
        ) {
          dispatch({ type: ActionType.SetParty, party: updatedParty });
        }
      });
      const willUnmount = () => {
        detachListener();
      };
      return willUnmount;
    }
  }, [partyKey, party, dispatch]);

  const userId = user?.uid;
  const isBanned =
    joinError === 'banned' ||
    (userId && (party?.stage?.participants?.[userId]?.tags || []).includes('banned'));

  if (isBanned) {
    return <BannedFromParty />;
  }

  if (user && party && joinStatus === 'connected') {
    if (isMobile) {
      history.push('/b/mobile-users');
      return <div>Sorry, but Backroom is not available on mobile just yet</div>;
    }

    if (party.password && password !== party.password) {
      return (
        <div className='onboarding-container'>
          <HomeButton />
          <div className='onboarding-card'>
            <h1>This party is password protected</h1>
            <h3>Please enter it below</h3>
            <br />
            <Input
              style={{ textAlign: 'center', height: 50 }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
      );
    }

    return (
      <>
        <VideoControlProvider>
          {!user.hasAcceptedProfile && <OnboardAnonymousUser />}
          {(joinError === 'no-authorization' || joinError === 'not-found') && <FourOhFour />}
          {!!user.hasAcceptedProfile && !isBanned && (
            <PartyStage
              initialArea={joinAreaKey}
              party={party}
              stage={party.stage}
              features={{
                roundAvatars: !!round || false,
                volumeControlFromPeer: !!vfp || false,
                volumeControlTowardsPeer: !!vtp || false,
              }}
            />
          )}
          <Notifications party={party} />
        </VideoControlProvider>
      </>
    );
  }

  if (joinStatus === 'connecting') {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Loading />
      </div>
    );
  }

  if (joinError === 'not-found') {
    return <PartyNotFound />;
  }

  return (
    <div>
      This party is full at the moment.
      <br />
      <button onClick={() => setJoinStatus('connecting')}>Try again</button>
    </div>
  );
};

export default connect(AppMapProps, (dispatch) => ({
  setInvitesAction: (invites: TParticipantInviteForUserMap) => dispatch(setInvites(invites)),
}))(PartyPage);
